import { useCallback } from 'react';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import isClickedOutside from 'src/js/modules/context-menu/verifiers/is-clicked-outside';
import isEscPressed from 'src/js/modules/context-menu/verifiers/is-esc-pressed';

/**
 * Escape / outside-click dismissal for the floating dropdown panel. Replaces
 * what `ws-ui`'s `Dropdown` used to give us via `useDismiss`.
 *
 * Intentionally not wired to the global `ws-dropdown:hide` event any more —
 * opening an unrelated dropdown should no longer close the chat.
 *
 * Clicks inside the chat iframe do not surface as document-level events in the
 * parent, so they can never be mistaken for an outside click. For the same
 * reason there is deliberately no focus/blur based fallback: focusing the
 * iframe would otherwise close the panel as soon as the user starts typing.
 */
const useAiChatDismissal = ({ enabled, refs, onDismiss }) => {
  const handleMousedown = useCallback(
    (event) => {
      if (!enabled) return;

      // A ref with no node cannot have been clicked, so it counts as outside.
      const isClickedOutsideAll = refs.every(
        (ref) => !ref.current || isClickedOutside(event, ref.current)
      );

      if (!isClickedOutsideAll) return;

      onDismiss();
    },
    [enabled, refs, onDismiss]
  );

  const handleKeydown = useCallback(
    (event) => {
      if (!enabled || !isEscPressed(event)) return;

      onDismiss();
    },
    [enabled, onDismiss]
  );

  useEventListener('mousedown', handleMousedown);
  useEventListener('keydown', handleKeydown);
};

export default useAiChatDismissal;
